import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box,
    Typography,
    TextField,
    MenuItem,
    Card,
    CardContent,
    Button,
    IconButton,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    Collapse,
    InputAdornment,
    FormControl,
    Select,
    Checkbox,
    FormControlLabel,
    InputBase,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    CalendarMonth,
    RemoveCircleOutline,
    AddCircleOutline,
    Refresh,
    PersonOff,
    Add,
    Search as SearchIcon,
    Clear as ClearIcon,
    CheckCircle,
    Check,
    Close,
    Phone as PhoneIcon,
    PauseCircleOutline,
    WhatsApp as WhatsAppIcon,
    CurrencyRupee,
    Edit as EditIcon,
} from '@mui/icons-material';
import { TOUCH, WHATSAPP } from '../theme/tokens';
import { invoiceAPI, businessAPI } from '../api';
import { sortAreasByRoute } from '../utils/routeOrder';
import { STATUS_TAGS, statusTagColor } from '../utils/customerTags';
import ScrollStrip from './common/ScrollStrip';
import AreaFilterRow from './common/AreaFilterRow';
import DailyOpsProductPicker, { lineOf } from './DailyOpsProductPicker';
import DailyOpsPaymentDialog from './DailyOpsPaymentDialog';
import { formatINR } from './common/Money';
import { useTranslation } from 'react-i18next';

// Sentinel for the "customers who belong to no area" filter chip. Not a real area id.
const NO_AREA = 'none';
// Sentinel for "houses nobody is assigned to". Member ids are numeric strings, so a
// non-numeric value cannot collide with one.
const NO_AGENT = 'noagent';
// The round opens on the houses that actually have something to hand over today.
// Paused, New (no plans yet) and Inactive are all "nothing to deliver", so they
// start off rather than making the vendor deselect three chips every morning.
const DEFAULT_STATUS_TAGS = ['Active', 'Part Paused'];
// Rupees for the cards: whole rupees stay whole, paise only when there are some.
const rupees = (n) => formatINR(n, { decimals: Number.isInteger(Math.round((Number(n) || 0) * 100) / 100) ? 0 : 2 });

// What one line comes to today: plan qty plus More, minus Less, at the day's rate. An absent
// line is 0; a paused one counts only what was added on top. Extras are recorded at the same rate × qty, so this matches the bill.
// Counts read as whole numbers where they are whole: 2, not 2.000.
const fmtCount = (q) => { const n = Number(q); return Number.isFinite(n) ? String(Number(n.toFixed(3))) : String(q ?? ''); };

const lineToday = (sub) => {
    const txns = sub.recorded_txns || [];
    const rate = sub.price_per_unit || 0;
    if (txns.some(t => t.origin === 'Absent')) return { qty: 0, rate, amount: 0 };
    const extra = txns.filter(t => t.origin === 'Extra').reduce((n, t) => n + (t.qty || 0), 0);
    const less = txns.filter(t => t.origin === 'Less').reduce((n, t) => n + Math.abs(t.qty || 0), 0);
    const qty = (sub.is_suspended ? 0 : (sub.subscribed_qty || 0)) + extra - less;
    return { qty, rate, amount: qty * rate };
};

// Summary heading for products with no category — matches the chip in the item picker and the PDF.
const OTHER_CATEGORY = 'No category';

// The strip covers a month back and the same fortnight forward the arrows allowed. Anything
// older is reached with the calendar button rather than by scrolling forever.
const STRIP_DAYS_BACK = 30;
const STRIP_DAYS_FORWARD = 15;

const toISO = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// App language -> a locale Intl understands, so day and month names follow the vendor's choice.
const localeOf = (lang) => ({ te: 'te-IN', hi: 'hi-IN' }[(lang || 'en').slice(0, 2)] || 'en-IN');

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

// Plans that can actually be marked absent: flat-fee ones are rejected by the server, and a
// suspended or zero-quantity plan isn't being delivered anyway.
const activeSubsOf = (cust) => (cust.subscriptions || []).filter(
    s => !s.is_suspended && s.subscribed_qty > 0 && s.billing_type !== 'MonthlyFlatFee'
);

// A house counts as not delivered only when every one of those plans is marked absent.
const isAllAbsent = (cust) => {
    const active = activeSubsOf(cust);
    return active.length > 0 && active.every(s =>
        (s.recorded_txns || []).some(t => t.origin === 'Absent'));
};

// Delivered is a stored tick, not-delivered is the absent txns, and anything else is a house
// the round hasn't reached yet. Shared so the cards and the filter chips can't disagree.
const DELIVERED = 'delivered', NOT_DELIVERED = 'not_delivered', PENDING = 'pending';
const deliveryStateOf = (cust) =>
    cust.delivered ? DELIVERED : isAllAbsent(cust) ? NOT_DELIVERED : PENDING;

const extractError = (e, fallback) => {
    const detail = e?.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
    return JSON.stringify(detail);
};

export default function DailyOpsPage({ businessId, business, members = [], onShowBars }) {
    const { t, i18n } = useTranslation();
    const [date, setDate] = useState(todayStr());
    const [areas, setAreas] = useState([]);
    const [opsAreaFilter, setOpsAreaFilter] = useState('');
    const [opsAgentFilter, setOpsAgentFilter] = useState('');
    // Plan status is multi-select and starts on the houses that actually have something to
    // deliver. Paused, New (no plans yet) and Inactive are all "nothing to hand over today",
    // so the round opens without them rather than making the vendor deselect three chips
    // every morning. An empty selection means no status filter at all — that is how the
    // "All" chip brings the excluded ones back into view.
    const [opsStatusTags, setOpsStatusTags] = useState(DEFAULT_STATUS_TAGS);
    const [opsDeliveryFilter, setOpsDeliveryFilter] = useState('');
    // Only houses whose customer has no phone (has_mobile false) — someone to ask for a number
    const [opsNoPhone, setOpsNoPhone] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    // 'route' = the order the round walks (what the server already returns), 'name' = A-Z.
    const [sortBy, setSortBy] = useState('route');
    const [customers, setCustomers] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // Editing a card's items in place — one card at a time. editQty holds typed text per product.
    const [editingCustId, setEditingCustId] = useState(null);
    const [editQty, setEditQty] = useState({});
    const [editSaving, setEditSaving] = useState(false);
    const [editFocusId, setEditFocusId] = useState(null);   // line whose box gets the cursor
    const [actionLoading, setActionLoading] = useState({});
    // Customer whose items are open in the product picker (plan items + other products)
    const [pickerCustomer, setPickerCustomer] = useState(null);
    const [summaryOpen, setSummaryOpen] = useState(true);
    // Show 50 rows at a time, like the other card lists
    const [visibleCount, setVisibleCount] = useState(50);
    // Plans on a schedule only show on the days they deliver. showAll brings the rest back so an
    // off-schedule delivery can still be recorded.
    const [showAll, setShowAll] = useState(false);
    const [hiddenCount, setHiddenCount] = useState(0);
    // Vendor's saved category order (Products page), so the summary groups read the same way.
    const [categoryOrder, setCategoryOrder] = useState([]);

    useEffect(() => {
        if (!businessId) return;
        businessAPI.getBusinessAreas(businessId)
            .then(r => setAreas(r.data || []))
            .catch(() => {});
        // Real products only — this list feeds the "Add Extra Product" picker, and an extra is a
        // one-off delivery, so the schedule a plan carries is irrelevant to it. Offering both
        // "Curd" and "Sunday Curd" would just be the same item at the same price listed twice.
        businessAPI.getBusinessProducts(businessId)
            .then(r => setAllProducts((r.data || []).filter(p => !p.f_parent_business_product_id)))
            .catch(() => {});
    }, [businessId]);

    // Plan status comes with the round itself, so it reflects the date being viewed.
    // v_customer_tags (what the Plans page uses) can only ever answer for today.
    const statusTagMap = useMemo(
        () => Object.fromEntries(customers.map(c => [c.customer_id, c.status_tag])),
        [customers]
    );

    // Tapping the arrows quickly fires one request per tap with nothing cancelling the last.
    // A slow reply for an earlier date could land after a fast one and paint the wrong day's
    // round, so only the newest request is allowed to write state.
    const loadSeq = useRef(0);

    // quiet: refresh in place after an action (tick, absent, save items) without the full-page
    // spinner. The spinner swaps out the whole list, the page shrinks, and the browser jumps back
    // to the top — losing the card the vendor was on.
    const load = useCallback(async ({ quiet = false } = {}) => {
        if (!businessId || !date) return;
        const seq = ++loadSeq.current;
        if (!quiet) setLoading(true);
        setError('');
        try {
            const r = await invoiceAPI.getDailyOps(businessId, date, null, null, showAll);
            if (seq !== loadSeq.current) return;   // superseded — drop it
            setCustomers(r.data.customers || []);
            setHiddenCount(r.data.hidden_count || 0);
            setCategoryOrder(r.data.category_order || []);
        } catch (e) {
            if (seq !== loadSeq.current) return;
            setError(extractError(e, 'Failed to load daily ops'));
        } finally {
            if (seq === loadSeq.current) setLoading(false);
        }
    }, [businessId, date, showAll]);

    useEffect(() => { load(); }, [load]);

    // ── Payments collected on the round ──
    // customer_id -> what was collected on the round for the day being viewed, so a card can
    // say "Paid ₹418" and nobody collects twice. Matched on the delivery day the collection was
    // for (f_for_date), not when it was entered — yesterday's round entered this morning still
    // shows on yesterday.
    const [paidByCustomer, setPaidByCustomer] = useState({});
    const [payCustomer, setPayCustomer] = useState(null);   // card open in the payment dialog
    const paidSeq = useRef(0);
    const loadPaid = useCallback(async () => {
        if (!businessId || !date) return;
        const seq = ++paidSeq.current;
        const paid = {};
        try {
            for (let page = 1; page <= 20; page++) {
                const res = await invoiceAPI.listPayments(businessId, { for_date: date, page, per_page: 100 });
                const rows = res.data || [];
                for (const p of rows) {
                    paid[p.f_business_customer_id] = (paid[p.f_business_customer_id] || 0) + Number(p.f_amount || 0);
                }
                if (rows.length < 100) break;
            }
        } catch (_) {
            // Not critical — the cards just won't show what was paid.
        }
        if (seq === paidSeq.current) setPaidByCustomer(paid);
    }, [businessId, date]);

    useEffect(() => { loadPaid(); }, [loadPaid]);

    const maxFutureStr = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() + STRIP_DAYS_FORWARD);
        return toISO(d);
    }, []);
    const isToday = date === todayStr();

    // Day and month names in the app's language rather than a hardcoded English table.
    const dayFmt = useMemo(
        () => new Intl.DateTimeFormat(localeOf(i18n.language), { weekday: 'short' }), [i18n.language]);
    const monthLabel = useMemo(() => new Intl.DateTimeFormat(localeOf(i18n.language),
        { month: 'long', year: 'numeric' }).format(new Date(date + 'T00:00:00')), [date, i18n.language]);

    // The days offered in the strip.
    const stripDays = useMemo(() => {
        const out = [];
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        start.setDate(start.getDate() - STRIP_DAYS_BACK);
        for (let i = 0; i <= STRIP_DAYS_BACK + STRIP_DAYS_FORWARD; i++) {
            const d = new Date(start);
            d.setDate(start.getDate() + i);
            out.push({ iso: toISO(d), d });
        }
        return out;
    }, []);


    // Area alone. Only used for the area chip counts and for working out which agents have
    // houses here — everything else works off the area+agent scope below.
    const areaFilteredCustomers = useMemo(() => {
        if (!opsAreaFilter) return customers;
        return opsAreaFilter === NO_AREA
            ? customers.filter(c => !c.area_id)
            : customers.filter(c => String(c.area_id) === String(opsAreaFilter));
    }, [customers, opsAreaFilter]);

    // Area and agent are both round-defining: "Sec B" and "Ramesh" together are one round,
    // and they combine rather than replacing each other. The summary follows this scope, so
    // it answers "how much does Ramesh load for Sec B" — which is also exactly what the
    // shared PDF totals, so screen and PDF cannot disagree. The chips below it (delivery
    // state, plan status) narrow the list only and must not move these numbers.
    // Area + agent. Agent chip counts are drawn from this level, before the status cut.
    const agentScoped = useMemo(() => {
        if (!opsAgentFilter) return areaFilteredCustomers;
        if (opsAgentFilter === NO_AGENT) return areaFilteredCustomers.filter(c => !c.agent_id);
        return areaFilteredCustomers.filter(c => String(c.agent_id) === String(opsAgentFilter));
    }, [areaFilteredCustomers, opsAgentFilter]);

    // ...plus plan status. Status belongs in the round definition, not with the narrowing
    // chips: leaving Inactive houses out of the list but inside the round size would have
    // the progress bar counting houses the vendor can never tick off. Delivery state is the
    // one filter that stays out of here — "Pending" asks what is left of the round, so it
    // must not shrink the round it is measuring against.
    const scopedCustomers = useMemo(() => {
        if (!opsStatusTags.length) return agentScoped;
        return agentScoped.filter(c => opsStatusTags.includes(statusTagMap[c.customer_id]));
    }, [agentScoped, opsStatusTags, statusTagMap]);

    // -- Summary --
    const summary = useMemo(() => {
        const byProduct = {};   // name -> { qty, amount }  (regular deliveries)
        const byExtra = {};     // name -> { qty, amount }
        const byLess = {};      // name -> { qty, amount }
        const bySuspended = {}; // name -> count
        const meta = {};        // name -> { category, rank } — for grouping and ordering below
        let suspended = 0, absentCustomers = 0, extraAmount = 0, lessAmount = 0;

        // Days in the selected month — used to prorate MonthlyFlatFee amounts
        const [yr, mo] = date.split('-').map(Number);
        const daysInMonth = new Date(yr, mo, 0).getDate();

        let deliveredCustomers = 0;
        for (const c of scopedCustomers) {
            if (c.delivered) deliveredCustomers++;
            let custHasAbsent = false;
            for (const s of c.subscriptions || []) {
                const name = s.product_name || 'Unknown';
                if (!meta[name]) meta[name] = { category: s.category || null, rank: s.product_rank ?? 1e9 };
                if (s.is_suspended) {
                    suspended++;
                    bySuspended[name] = (bySuspended[name] || 0) + 1;
                    continue;
                }
                const isAbsent = (s.recorded_txns || []).some(t => t.origin === 'Absent');
                if (isAbsent) custHasAbsent = true;
                if (!isAbsent && s.subscribed_qty > 0) {
                    // MonthlyFlatFee: price_per_unit is the full monthly amount;
                    // show the prorated daily contribution in the summary.
                    const rawPrice = s.price_per_unit || 0;
                    const price = s.billing_type === 'MonthlyFlatFee'
                        ? rawPrice / daysInMonth
                        : rawPrice;
                    if (!byProduct[name]) byProduct[name] = { qty: 0, amount: 0 };
                    byProduct[name].qty += s.subscribed_qty;
                    byProduct[name].amount += s.subscribed_qty * price;
                }
                for (const t of s.recorded_txns || []) {
                    if (t.origin === 'Extra') {
                        if (!byExtra[name]) byExtra[name] = { qty: 0, amount: 0 };
                        byExtra[name].qty += t.qty || 0;
                        byExtra[name].amount += Math.abs(t.amount || 0);
                        extraAmount += Math.abs(t.amount || 0);
                    }
                    if (t.origin === 'Less') {
                        if (!byLess[name]) byLess[name] = { qty: 0, amount: 0 };
                        byLess[name].qty += Math.abs(t.qty || 0);
                        byLess[name].amount += Math.abs(t.amount || 0);
                        lessAmount += Math.abs(t.amount || 0);
                    }
                }
            }
            if (custHasAbsent) absentCustomers++;
        }
        const regularQty = Object.values(byProduct).reduce((s, v) => s + v.qty, 0);
        const regularAmount = Object.values(byProduct).reduce((s, v) => s + v.amount, 0);
        const extraQty = Object.values(byExtra).reduce((s, v) => s + v.qty, 0);
        const lessQty = Object.values(byLess).reduce((s, v) => s + v.qty, 0);
        const totalQty = regularQty + extraQty - lessQty;
        const totalAmount = regularAmount + extraAmount - lessAmount;

        // Group by category for the card: the vendor's saved category order first, then any
        // others in product order, uncategorised last. Products inside a group keep the
        // vendor's product order. A product in several categories sits under its first one
        // (the server sends only that), so nothing is counted twice.
        // Absent-only products have no line to show, so they must not leave an empty heading.
        const names = Object.keys(meta)
            .filter(n => byProduct[n] || byExtra[n] || byLess[n] || bySuspended[n])
            .sort((a, b) => meta[a].rank - meta[b].rank);
        const groupMap = {};
        const appearance = [];
        for (const name of names) {
            const cat = meta[name].category || OTHER_CATEGORY;
            if (!groupMap[cat]) {
                groupMap[cat] = { category: cat, products: [], qty: 0, amount: 0 };
                appearance.push(cat);
            }
            const g = groupMap[cat];
            g.products.push(name);
            g.qty += (byProduct[name]?.qty || 0) + (byExtra[name]?.qty || 0) - (byLess[name]?.qty || 0);
            g.amount += (byProduct[name]?.amount || 0) + (byExtra[name]?.amount || 0) - (byLess[name]?.amount || 0);
        }
        const ordered = [
            ...categoryOrder.filter(c => groupMap[c] && c !== OTHER_CATEGORY),
            ...appearance.filter(c => c !== OTHER_CATEGORY && !categoryOrder.includes(c)),
            ...(groupMap[OTHER_CATEGORY] ? [OTHER_CATEGORY] : []),
        ];
        const groups = ordered.map(c => groupMap[c]);
        // Headers only help when there is more than one thing to tell apart.
        const showCategories = groups.length > 1 || (groups.length === 1 && groups[0].category !== OTHER_CATEGORY);

        return { byProduct, byExtra, byLess, bySuspended, suspended, absentCustomers,
            deliveredCustomers, extraAmount, lessAmount, totalQty, totalAmount, groups, showCategories };
    }, [scopedCustomers, date, categoryOrder]);

    const setLoaderFor = (key, val) =>
        setActionLoading(prev => ({ ...prev, [key]: val }));

    const displayedCustomers = useMemo(() => {
        // Already narrowed to the chosen area, so the list and the summary above it can never
        // disagree about which houses are in scope.
        let list = scopedCustomers;
        if (opsDeliveryFilter) {
            list = list.filter(c => deliveryStateOf(c) === opsDeliveryFilter);
        }
        if (opsNoPhone) {
            list = list.filter(c => c.has_mobile === false);
        }
        const q = searchQuery.trim().toLowerCase();
        if (q) {
            list = list.filter(c =>
                c.customer_name?.toLowerCase().includes(q) ||
                c.customer_mobile?.includes(q)
            );
        }
        // Route order is what the server already sent, so only a name sort needs work here.
        if (sortBy === 'name') {
            list = [...list].sort((a, b) => (a.customer_name || '').localeCompare(b.customer_name || ''));
        }
        return list;
    }, [scopedCustomers, searchQuery, opsDeliveryFilter, opsNoPhone, sortBy]);

    // ── Share the round as a PDF ──────────────────────────────────────────
    // Agents mostly never log in, so the round leaves the app as a file the vendor sends
    // from their own WhatsApp — to a registered agent, or to whoever is standing in today
    // and is not in the system at all. A file, not a link: nothing to sign into, nothing
    // to expire, and it still works where there is no signal on the route.
    const [sharing, setSharing] = useState(false);
    // Customer phone numbers on a list that gets forwarded to whoever is covering the
    // round today is a real exposure, so it is off unless the vendor asks for it.
    // Remembered per device: this is a standing preference, not a per-send decision.
    const [includePhones, setIncludePhones] = useState(() => {
        try { return localStorage.getItem('vendora.round.phones') === '1'; } catch { return false; }
    });
    const togglePhones = (on) => {
        setIncludePhones(on);
        try { localStorage.setItem('vendora.round.phones', on ? '1' : '0'); } catch (_) { /* private mode */ }
    };

    // Same rules the list itself uses: a suspended plan is not delivered, an Absent txn
    // zeroes the line, Extra adds and Less subtracts. Kept next to the summary that
    // applies them so the two cannot drift.
    const buildRoundRows = () => displayedCustomers.map(c => {
        const items = [];
        for (const s of c.subscriptions || []) {
            const name = s.product_name || 'Unknown';
            const where = { category: s.category || null, rank: s.product_rank ?? null };
            if (s.is_suspended) { items.push({ name, qty: 0, note: 'Paused', ...where }); continue; }
            const txns = s.recorded_txns || [];
            const isAbsent = txns.some(t => t.origin === 'Absent');
            const extra = txns.filter(t => t.origin === 'Extra').reduce((n, t) => n + (t.qty || 0), 0);
            const less = txns.filter(t => t.origin === 'Less').reduce((n, t) => n + Math.abs(t.qty || 0), 0);
            if (isAbsent) { items.push({ name, qty: 0, note: 'Absent', ...where }); continue; }
            const qty = (s.subscribed_qty || 0) + extra - less;
            if (qty <= 0 && !extra && !less) continue;
            const notes = [];
            if (extra) notes.push(`+${extra} extra`);
            if (less) notes.push(`-${less} less`);
            items.push({ name, qty, note: notes.join(', ') || null, ...where });
        }
        return {
            name: c.customer_name || '',
            mobile: includePhones ? (c.customer_mobile || null) : null,
            address: c.customer_address_line1 || null,
            area: areas.find(a => a.f_id === c.area_id)?.f_name || null,
            delivered: !!c.delivered,
            items,
        };
    });

    // What the recipient needs to know about which slice of the round this is.
    const filterLabel = () => {
        const bits = [];
        const area = areas.find(a => a.f_id === Number(opsAreaFilter));
        if (area) bits.push(area.f_name);
        if (opsAgentFilter === NO_AGENT) {
            bits.push('No agent');
        } else {
            const agent = members.find(m => String(m.f_id) === String(opsAgentFilter));
            if (agent) bits.push(agent.f_name || agent.f_mobile);
        }
        if (opsStatusTags.length) bits.push(opsStatusTags.join(' + '));
        if (opsDeliveryFilter) bits.push(opsDeliveryFilter);
        if (opsNoPhone) bits.push('No phone');
        if (searchQuery.trim()) bits.push(`"${searchQuery.trim()}"`);
        return bits.join('  -  ') || null;
    };

    const handleShareRound = async () => {
        // Deliberately the whole filtered set, not the paginated slice — "what he sees"
        // means what he filtered to, not how far he happens to have scrolled.
        const rows = buildRoundRows();
        if (!rows.length) { setError('Nothing to send — no houses in this list.'); return; }
        setSharing(true);
        setError('');
        try {
            const res = await invoiceAPI.getDailyOpsPdf(businessId, {
                ops_date: date, filter_label: filterLabel(), rows, category_order: categoryOrder,
            });
            const file = new File([res.data], `round-${date}.pdf`, { type: 'application/pdf' });

            if (navigator.canShare?.({ files: [file] })) {
                // Opens the OS share sheet — the vendor picks WhatsApp and then any
                // contact, so a stand-in agent needs no record in the app at all.
                await navigator.share({ files: [file], title: `Delivery list ${date}` });
            } else {
                // Desktop, or a browser without file sharing: hand over the file and let
                // them attach it themselves.
                const url = URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.href = url; a.download = `round-${date}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 10000);
            }
        } catch (e) {
            // A cancelled share sheet is a normal user action, not a failure.
            if (e?.name === 'AbortError') return;
            // responseType 'blob' means an error body arrives as a Blob, so the usual
            // e.response.data.detail is always undefined — read it back as text or the
            // server's message is lost.
            let detail = '';
            try {
                if (e.response?.data instanceof Blob) {
                    const parsed = JSON.parse(await e.response.data.text());
                    detail = typeof parsed.detail === 'string' ? parsed.detail : '';
                } else if (typeof e.response?.data?.detail === 'string') {
                    detail = e.response.data.detail;
                }
            } catch (_) { /* not JSON — fall through to the generic message */ }
            console.error('Share round failed:', e);
            setError(detail || 'Could not build the delivery list.');
        } finally {
            setSharing(false);
        }
    };

    // Restart pagination whenever the visible set changes, and make sure the app bars are
    // back. They hide on scroll-down, and only a scroll brings them back — but a filter tap
    // is not a scroll, so the vendor could be left tapping chips with no bottom navigation
    // at all. Worse, when a chip shrinks the list below one screen the browser clamps the
    // scroll to 0, and that clamp fires the scroll handler, so the bars lurch back in a
    // moment later and the tap looks like it broke the page.
    useEffect(() => {
        setVisibleCount(50);
        if (onShowBars) onShowBars();
    },  // eslint-disable-next-line react-hooks/exhaustive-deps
        [searchQuery, opsAreaFilter, opsAgentFilter, opsStatusTags, opsDeliveryFilter, opsNoPhone, sortBy, date]);

    const undoTxn = async (txnId) => {
        const key = `undo-${txnId}`;
        setLoaderFor(key, true);
        try {
            await invoiceAPI.deleteDailyOpsEntry(businessId, txnId);
            await load({ quiet: true });
        } catch (e) {
            setError(extractError(e, 'Failed to undo'));
        } finally {
            setLoaderFor(key, false);
        }
    };

    // Mark ALL active subscriptions for a customer as absent (PerEachItem only)
    const markCustomerAllAbsent = async (cust) => {
        const toAbsent = (cust.subscriptions || []).filter(s =>
            !s.is_suspended && s.subscribed_qty > 0 &&
            s.billing_type !== 'MonthlyFlatFee' &&
            !(s.recorded_txns || []).some(t => t.origin === 'Absent')
        );
        // Delivered and absent are opposite claims about the same house, so a stale tick has to
        // go even when there is nothing left to mark absent.
        if (!toAbsent.length && !cust.delivered) return;
        const key = `allabsent-${cust.customer_id}`;
        setLoaderFor(key, true);
        try {
            if (cust.delivered) {
                await invoiceAPI.unmarkDelivered(businessId, cust.customer_id, date);
            }
            await Promise.all(toAbsent.map(s =>
                invoiceAPI.recordDailyOpsEntry(businessId, {
                    customerId: cust.customer_id,
                    productId: s.product_id,
                    opsDate: date,
                    origin: 'Absent',
                    quantity: s.subscribed_qty,
                })
            ));
            await load({ quiet: true });
        } catch (e) {
            setError(extractError(e, 'Failed to mark customer absent'));
        } finally {
            setLoaderFor(key, false);
        }
    };

    // Tick a house off the round, or clear the tick. Purely informational: unlike an Absent
    // entry this never reaches the bill, so a house left unticked is still charged in full.
    const setDelivered = async (cust, delivered) => {
        const key = `delivered-${cust.customer_id}`;
        setLoaderFor(key, true);
        try {
            if (delivered) {
                // Clearing any absents first keeps the two states mutually exclusive. Uses the
                // existing soft-delete, so the billing semantics are unchanged.
                const absentTxns = (cust.subscriptions || []).flatMap(s =>
                    (s.recorded_txns || []).filter(t => t.origin === 'Absent')
                );
                if (absentTxns.length) {
                    await Promise.all(absentTxns.map(t =>
                        invoiceAPI.deleteDailyOpsEntry(businessId, t.txn_id)
                    ));
                }
                await invoiceAPI.markDelivered(businessId, cust.customer_id, date);
            } else {
                await invoiceAPI.unmarkDelivered(businessId, cust.customer_id, date);
            }
            await load({ quiet: true });
        } catch (e) {
            setError(extractError(e, 'Failed to update delivery'));
        } finally {
            setLoaderFor(key, false);
        }
    };

    // Undo ALL absent txns for a customer
    const undoAllAbsent = async (cust) => {
        const absentTxns = (cust.subscriptions || []).flatMap(s =>
            (s.recorded_txns || []).filter(t => t.origin === 'Absent')
        );
        if (!absentTxns.length) return;
        const key = `undoallabsent-${cust.customer_id}`;
        setLoaderFor(key, true);
        try {
            await Promise.all(absentTxns.map(t =>
                invoiceAPI.deleteDailyOpsEntry(businessId, t.txn_id)
            ));
            await load({ quiet: true });
        } catch (e) {
            setError(extractError(e, 'Failed to undo absent'));
        } finally {
            setLoaderFor(key, false);
        }
    };


    // Each change from the picker replaces that product's Extra/Less entries for the day with
    // one entry for the difference from the plan. Absent entries are never touched here.
    const startEdit = (cust, productId = null) => {
        if (editSaving) return;
        setEditingCustId(cust.customer_id);
        setEditQty({});
        setEditFocusId(productId);
    };
    const cancelEdit = () => { setEditingCustId(null); setEditQty({}); };

    // Save a card edited in place: each line whose count moved becomes one change, saved the same
    // way as the item picker (old More/Less entries replaced by one entry for the difference).
    const saveEdit = async (cust, liveSubs) => {
        const changes = liveSubs
            .filter(sub => sub.billing_type !== 'MonthlyFlatFee')
            .map(sub => ({ sub, line: lineOf(sub) }))
            .filter(({ line }) => !line.isAbsent)
            .map(({ sub, line }) => {
                const typed = editQty[sub.product_id];
                const n = typed === undefined ? line.current : parseFloat(typed);
                return { productId: sub.product_id, base: line.base, current: line.current,
                         target: Number.isFinite(n) ? Math.max(0, n) : 0, undoTxnIds: line.adjustTxnIds };
            })
            .filter(ch => ch.target !== ch.current);
        if (!changes.length) { cancelEdit(); return; }
        setEditSaving(true);
        try {
            await saveItemChanges(cust, changes);
        } finally {
            setEditSaving(false);
            cancelEdit();
        }
    };

    // A different day is a different round: leave edit mode.
    useEffect(() => { cancelEdit(); }, [date]);  // eslint-disable-line react-hooks/exhaustive-deps

    const saveItemChanges = async (customer, changes) => {
        try {
            for (const ch of changes) {
                for (const txnId of ch.undoTxnIds) {
                    await invoiceAPI.deleteDailyOpsEntry(businessId, txnId);
                }
                const diff = ch.target - ch.base;
                if (diff !== 0) {
                    await invoiceAPI.recordDailyOpsEntry(businessId, {
                        customerId: customer.customer_id, productId: ch.productId, opsDate: date,
                        origin: diff > 0 ? 'Extra' : 'Less', quantity: Math.abs(diff),
                    });
                }
            }
            setPickerCustomer(null);
        } catch (e) {
            setError(extractError(e, 'Failed to save items'));
            setPickerCustomer(null);
        } finally {
            await load({ quiet: true });
        }
    };

    return (
        <Box sx={{ p: 2, pb: 10 }}>
            {/* Date navigation — a strip of named, numbered days.
                Bare ‹ › chevrons are a learned convention: they carry no meaning on their own
                and nothing on screen said what they changed. Tapping the day you want needs
                nothing learned, and the strip also shows where you currently are. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}
                    sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {monthLabel}
                </Typography>
                {!isToday && (
                    // Walking back from a far date used to be as many taps as getting there.
                    <Chip size="small" label={t('dailyOps.today')} color="primary" variant="outlined"
                        clickable onClick={() => setDate(todayStr())}
                        sx={{ height: 20, fontSize: '0.65rem' }} />
                )}
            </Box>

            <ScrollStrip
                items={stripDays.map(({ iso, d }) => ({
                    key: iso,
                    top: dayFmt.format(d),
                    bottom: d.getDate(),
                    accent: iso === todayStr(),      // outline today so it stays findable
                }))}
                selectedKey={date}
                onSelect={(iso) => setDate(iso)}
                endAdornment={
                    /* Anything older than the strip: the phone's own calendar. */
                    <Box sx={{ position: 'relative', flexShrink: 0 }}>
                        <IconButton size="small" sx={{ color: 'text.secondary' }}><CalendarMonth /></IconButton>
                        <Box component="input" type="date" aria-label="Change date"
                            value={date} max={maxFutureStr}
                            onChange={e => { if (e.target.value) setDate(e.target.value); }}
                            sx={{
                                position: 'absolute', inset: 0, width: '100%', height: '100%',
                                opacity: 0, border: 0, p: 0, m: 0, cursor: 'pointer',
                                // iOS ignores width on date inputs unless this is cleared
                                WebkitAppearance: 'none', background: 'transparent',
                            }} />
                    </Box>
                }
            />
            <Box sx={{ mb: 1.5 }} />

            {/* Only shown once something is actually off-schedule today, so vendors with no
                scheduled plans never see a control they don't need. */}
            {(hiddenCount > 0 || showAll) && (
                <Box display="flex" justifyContent="center" mb={1}>
                    <Chip
                        size="small"
                        variant={showAll ? 'filled' : 'outlined'}
                        color={showAll ? 'primary' : 'default'}
                        onClick={() => setShowAll(v => !v)}
                        label={showAll ? "Showing all" : `Show all${hiddenCount ? ` (${hiddenCount})` : ''}`}
                        sx={{ height: 32 }}
                    />
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Area chips sit ABOVE the summary: picking an area re-totals the card below it, so
                the vendor can read off exactly what to load for that round. Kept separate from
                the chips under the summary, which only narrow the list. */}
            {!loading && (
                <AreaFilterRow
                    options={sortAreasByRoute(areas)
                        .map(a => ({
                            key: String(a.f_id),
                            label: a.f_name,
                            count: customers.filter(c => c.area_id === a.f_id).length,
                        }))
                        .filter(a => a.count > 0)}
                    noArea={{ key: NO_AREA, count: customers.filter(c => !c.area_id).length }}
                    total={customers.length}
                    value={opsAreaFilter}
                    onChange={setOpsAreaFilter}
                    sx={{ mb: 1.5 }}
                />
            )}

            {/* Agents sit with the areas, not with the narrowing chips below, because an
                area and an agent together are what define a round — "Sec B, Ramesh". They
                combine: picking one does not clear the other, and the summary above
                re-totals for both, so it answers what that agent loads for that area.
                Counts are within the chosen area; agents with no houses there drop out. */}
            {(() => {
                // c.agent_id is a business-member row id, so it matches m.f_id.
                const agentsInArea = [...new Map(
                    areaFilteredCustomers
                        .filter(c => c.agent_id)
                        .map(c => {
                            const m = members.find(m => String(m.f_id) === String(c.agent_id));
                            return [String(c.agent_id), m?.f_name || m?.f_mobile || String(c.agent_id)];
                        })
                ).entries()];
                if (agentsInArea.length === 0) return null;
                const unassigned = areaFilteredCustomers.filter(c => !c.agent_id).length;
                return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                        <Chip
                            label={`All Agents (${areaFilteredCustomers.length})`}
                            size="small"
                            color={!opsAgentFilter ? 'primary' : 'default'}
                            variant={!opsAgentFilter ? 'filled' : 'outlined'}
                            onClick={() => setOpsAgentFilter('')}
                            clickable
                        />
                        {agentsInArea.map(([id, name]) => {
                            const count = areaFilteredCustomers.filter(c => String(c.agent_id) === id).length;
                            const active = opsAgentFilter === id;
                            return (
                                <Chip
                                    key={id}
                                    label={`${name} (${count})`}
                                    size="small"
                                    color={active ? 'info' : 'default'}
                                    variant={active ? 'filled' : 'outlined'}
                                    onClick={() => setOpsAgentFilter(active ? '' : id)}
                                    clickable
                                />
                            );
                        })}
                        {unassigned > 0 && (
                            <Chip
                                label={`No Agent (${unassigned})`}
                                size="small"
                                color={opsAgentFilter === NO_AGENT ? 'info' : 'default'}
                                variant={opsAgentFilter === NO_AGENT ? 'filled' : 'outlined'}
                                onClick={() => setOpsAgentFilter(opsAgentFilter === NO_AGENT ? '' : NO_AGENT)}
                                clickable
                            />
                        )}
                    </Box>
                );
            })()}

            {/* Summary card — totals for the selected area */}
            {!loading && customers.length > 0 && (
                <Card variant="outlined" sx={{ mb: 1.5, bgcolor: 'background.default' }}>
                    {/* Collapsible header */}
                    <Box
                        display="flex" justifyContent="space-between" alignItems="center"
                        px={1.5} py={0.75}
                        onClick={() => setSummaryOpen(o => !o)}
                        sx={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                        <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Deliveries &middot; {summary.totalQty} items
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                                ₹{summary.totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">{summaryOpen ? '▲' : '▼'}</Typography>
                        </Box>
                    </Box>

                    {/* How far through the round we are. Stays visible when the summary is
                        collapsed — it is the thing a delivery boy checks mid-round. */}
                    {(() => {
                        // Both halves come from the same scope as the summary above, or the
                        // bar reads "0/8 done" for a three-house round once an agent is picked.
                        const handled = summary.deliveredCustomers + summary.absentCustomers;
                        const roundSize = scopedCustomers.length;
                        const pct = roundSize ? Math.round((handled / roundSize) * 100) : 0;
                        return (
                            <Box sx={{ px: 1.5, pb: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'divider', overflow: 'hidden' }}>
                                    <Box sx={{
                                        width: `${pct}%`, height: '100%',
                                        bgcolor: pct === 100 ? 'success.main' : 'primary.main',
                                        transition: 'width 200ms',
                                    }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ flexShrink: 0 }}>
                                    {handled}/{roundSize} {t('dailyOps.done')}
                                </Typography>
                            </Box>
                        );
                    })()}

                    <Collapse in={summaryOpen}>
                        <Divider />
                        <Box px={1.5} py={1}>
                            {summary.groups.length === 0 && (
                            <Typography variant="caption" color="text.secondary">{t('dailyOps.noDeliveries')}</Typography>
                            )}
                            {summary.groups.map((g, gi) => (
                                <Box key={`cat-${g.category}`} mt={summary.showCategories && gi > 0 ? 1 : 0}>
                                    {/* Category heading with its own total, so the vendor can load by category */}
                                    {summary.showCategories && (
                                        <Box display="flex" justifyContent="space-between" alignItems="center"
                                            sx={{ bgcolor: (th) => alpha(th.palette.primary.main, 0.08),
                                                  borderLeft: 3, borderColor: 'primary.main',
                                                  borderRadius: 0.5, px: 1, py: 0.4, mb: 0.25 }}>
                                            <Typography variant="subtitle2" fontWeight={800} color="primary.main"
                                                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: 1.3 }}>
                                                {g.category} <Typography component="span" variant="caption" fontWeight={700} color="primary.main">× {g.qty}</Typography>
                                            </Typography>
                                            <Typography variant="subtitle2" fontWeight={800} color="primary.main">
                                                ₹{g.amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                            </Typography>
                                        </Box>
                                    )}
                                    {/* Products indent under their heading */}
                                    <Box sx={{ pl: summary.showCategories ? 1.5 : 0 }}>
                                    {g.products.map(name => {
                                        const reg = summary.byProduct[name];
                                        const extra = summary.byExtra[name];
                                        const less = summary.byLess[name];
                                        const paused = summary.bySuspended[name];
                                        return (
                                            <React.Fragment key={name}>
                                                {reg && (
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                        <Typography variant="body2" color="text.primary">
                                                            {name} <Typography component="span" variant="caption" color="text.secondary">× {reg.qty}</Typography>
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500}>
                                                            ₹{reg.amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {extra && (
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                        <Typography variant="body2" color="success.main">
                                                            {name} <Typography component="span" variant="caption" color="success.light">+{extra.qty} extra</Typography>
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500} color="success.main">
                                                            +₹{extra.amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {less && (
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                        <Typography variant="body2" color="warning.dark">
                                                            {name} <Typography component="span" variant="caption" color="warning.main">{less.qty} less</Typography>
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={500} color="warning.dark">
                                                            -₹{less.amount.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {paused && (
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={0.2}>
                                                        <Typography variant="body2" color="text.disabled">
                                                            {name} <Typography component="span" variant="caption" color="text.disabled">× {paused} paused</Typography>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.disabled">—</Typography>
                                                    </Box>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    </Box>
                                </Box>
                            ))}
                            {summary.absentCustomers > 0 && (
                                <Box mt={0.75}>
                                    <Chip icon={<PersonOff fontSize="small" />} label={`${summary.absentCustomers} Absent`} color="error" size="small" />
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                </Card>
            )}

            {/* Search + Sort + Refresh row — same treatment as the Customers page */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center' }}>
                <TextField
                    size="small"
                    placeholder={t('dailyOps.searchCustomer')}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    sx={{
                        flex: 1, minWidth: 0,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                            bgcolor: 'white',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            border: '1.5px solid',
                            borderColor: searchQuery ? 'primary.main' : 'grey.200',
                            transition: 'border-color 0.2s, box-shadow 0.2s',
                            '& fieldset': { border: 'none' },
                            '&:hover': { borderColor: 'primary.light', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' },
                            '&.Mui-focused': { borderColor: 'primary.main', boxShadow: '0 0 0 3px rgba(37,99,235,0.12)' },
                        },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" sx={{ color: searchQuery ? 'primary.main' : 'text.disabled' }} />
                            </InputAdornment>
                        ),
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: 'text.secondary', p: 0.5 }}>
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                <FormControl size="small" sx={{ flexShrink: 0, width: 118 }}>
                    <Select
                        value={sortBy}
                        onChange={e => setSortBy(e.target.value)}
                        displayEmpty
                        renderValue={(v) => (
                            <Box sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled', lineHeight: 1 }}>Sort by</Typography>
                                <Typography variant="body2" noWrap sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                                    {v === 'name' ? t('customer.sort.name') : t('customer.sort.route')}
                                </Typography>
                            </Box>
                        )}
                        sx={{
                            borderRadius: 3,
                            bgcolor: 'white',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                            border: '1.5px solid',
                            // Highlight when the round is not in delivery order — that is the
                            // non-default state here, the opposite of the Customers page.
                            borderColor: sortBy !== 'route' ? 'primary.main' : 'grey.200',
                            '& fieldset': { border: 'none' },
                            '&:hover': { borderColor: 'primary.light' },
                            '& .MuiSelect-select': { py: '6px' },
                        }}
                    >
                        <MenuItem value="route">{t('customer.sort.route')}</MenuItem>
                        <MenuItem value="name">{t('customer.sort.name')}</MenuItem>
                    </Select>
                </FormControl>
                <IconButton onClick={load} title="Refresh" size="small" sx={{ flexShrink: 0 }}><Refresh /></IconButton>
            </Box>

            {/* Send the round to whoever is walking it. Sits under the filters because it
                sends exactly what they select — filters, search and sort included. */}
            {displayedCustomers.length > 0 && (
                <Button
                    onClick={handleShareRound}
                    disabled={sharing}
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={sharing ? <CircularProgress size={16} /> : <WhatsAppIcon />}
                    sx={{
                        mb: 1.5, textTransform: 'none', fontWeight: 600,
                        minHeight: TOUCH.dense, borderRadius: 2,
                        color: WHATSAPP.main, borderColor: WHATSAPP.main,
                        '&:hover': { borderColor: WHATSAPP.hover, bgcolor: `${WHATSAPP.main}14` },
                    }}
                >
                    {sharing ? 'Making the list…' : `Send this list (${displayedCustomers.length})`}
                </Button>
            )}
            {displayedCustomers.length > 0 && (
                <FormControlLabel
                    sx={{ mt: -1, mb: 1.5, ml: 0.25 }}
                    control={
                        <Checkbox
                            size="small"
                            checked={includePhones}
                            onChange={e => togglePhones(e.target.checked)}
                            sx={{ py: 0.5 }}
                        />
                    }
                    label={
                        <Typography variant="caption" color="text.secondary">
                            Put customer phone numbers in the list
                        </Typography>
                    }
                />
            )}

            {/* Delivery state + Status + Agent chips. These narrow the list only — area lives
                above the summary because it re-totals it. */}
            {(() => {
                // One of these applies at a time. Area and agent above are independent of
                // them and of each other — clearing here must not drop the chosen agent,
                // or picking a status would silently widen the round back out.
                const clearAll = () => {
                    setOpsStatusTags([]); setOpsDeliveryFilter(''); setOpsNoPhone(false);
                };
                const noPhoneCount = scopedCustomers.filter(c => c.has_mobile === false).length;
                // Counted within the chosen area and agent, so they agree with the list.
                const stateCounts = scopedCustomers.reduce((acc, c) => {
                    acc[deliveryStateOf(c)]++;
                    return acc;
                }, { [DELIVERED]: 0, [NOT_DELIVERED]: 0, [PENDING]: 0 });
                const deliveryChips = [
                    { key: DELIVERED, label: t('dailyOps.delivered'), color: 'success' },
                    { key: NOT_DELIVERED, label: t('dailyOps.notDelivered'), color: 'error' },
                    { key: PENDING, label: t('dailyOps.pending'), color: 'default' },
                ].filter(c => stateCounts[c.key] > 0);
                // Counted before the status cut — counting after it would show every
                // unselected tag as (0) and there would be no way to tell what you are
                // missing, or what tapping it would bring back.
                const statusChips = STATUS_TAGS
                    .map(tag => ({
                        tag,
                        count: agentScoped.filter(c => statusTagMap[c.customer_id] === tag).length,
                    }))
                    .filter(s => s.count > 0);
                const noFilter = !opsStatusTags.length && !opsDeliveryFilter && !opsNoPhone;
                if (statusChips.length === 0 && deliveryChips.length === 0) return null;
                // One wrapping row, not three. Delivery, status and agent chips used to
                // sit in separate flex containers, so each began a new line and each ended
                // in dead space — eight chips took four rows on a 334px strip when they
                // pack into three. Order is unchanged, so related chips stay adjacent; they
                // just flow on instead of breaking. Round progress still comes first:
                // "who is left" is the question asked most while walking.
                return (
                    <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                            <Chip
                                // The count of what tapping it would give you, not of what
                                // is showing now — otherwise it reads "All (10)" while
                                // hiding 13 houses.
                                label={`All (${agentScoped.length})`}
                                size="small"
                                color={noFilter ? 'primary' : 'default'}
                                onClick={clearAll}
                                clickable
                            />
                            {statusChips.map(({ tag, count }) => {
                                const active = opsStatusTags.includes(tag);
                                return (
                                    <Chip
                                        key={tag}
                                        label={`${tag} (${count})`}
                                        size="small"
                                        color={active ? statusTagColor(tag) : 'default'}
                                        variant={active ? 'filled' : 'outlined'}
                                        // Multi-select: a vendor wanting Active plus Part
                                        // Paused should not have to choose between them.
                                        onClick={() => setOpsStatusTags(prev =>
                                            prev.includes(tag)
                                                ? prev.filter(x => x !== tag)
                                                : [...prev, tag]
                                        )}
                                        clickable
                                    />
                                );
                            })}
                            {/* Two different questions, so they read as two sets: which
                                houses are on the round (status, above) and how far through
                                it you are (delivery, below). Kept inside the one wrapping
                                row so the chips still pack instead of each set claiming
                                its own line. */}
                            <Box sx={{ alignSelf: 'center', width: '1px', height: 18,
                                       bgcolor: 'divider', mx: 0.75, flexShrink: 0 }} />
                            {deliveryChips.map(({ key, label, color }) => {
                                const active = opsDeliveryFilter === key;
                                return (
                                    <Chip
                                        key={key}
                                        label={`${label} (${stateCounts[key]})`}
                                        size="small"
                                        color={active ? color : 'default'}
                                        variant={active ? 'filled' : 'outlined'}
                                        onClick={() => {
                                            clearAll();
                                            setOpsDeliveryFilter(active ? '' : key);
                                        }}
                                        clickable
                                    />
                                );
                            })}
                            {noPhoneCount > 0 && (
                                <Chip
                                    label={`No phone (${noPhoneCount})`}
                                    size="small"
                                    color={opsNoPhone ? 'primary' : 'default'}
                                    variant={opsNoPhone ? 'filled' : 'outlined'}
                                    onClick={() => setOpsNoPhone(v => !v)}
                                    clickable
                                />
                            )}
                        </Box>
                );
            })()}

            {loading ? (
                <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
            ) : displayedCustomers.length === 0 ? (
                <Alert severity="info">{searchQuery ? 'No customers match your search.' : 'No customers found.'}</Alert>
            ) : (
                <>
                {displayedCustomers.slice(0, visibleCount).map(cust => {
                    const activeSubs = activeSubsOf(cust);
                    const allAbsent = isAllAbsent(cust);
                    const allAbsentKey = `allabsent-${cust.customer_id}`;
                    const undoAllAbsentKey = `undoallabsent-${cust.customer_id}`;
                    // Delivered is a stored tick; not-delivered is the absent txns. Neither
                    // being set means the house simply has not been reached yet.
                    const isDelivered = !!cust.delivered;
                    const deliveredKey = `delivered-${cust.customer_id}`;
                    const cardBusy = !!actionLoading[deliveredKey] || !!actionLoading[allAbsentKey]
                        || !!actionLoading[undoAllAbsentKey];

                    return (
                        <Card key={cust.customer_id} variant="outlined" sx={{
                            mb: 0.75,
                            borderLeft: 4,
                            borderLeftColor: isDelivered ? 'success.main'
                                : allAbsent ? 'error.main' : 'divider',
                            borderColor: isDelivered ? 'success.light'
                                : allAbsent ? 'error.light' : 'divider',
                            // alpha(), not 'success.50' — this theme defines only main/dark, so
                            // numeric palette shades resolve to nothing and get dropped.
                            bgcolor: (th) => isDelivered ? alpha(th.palette.success.main, 0.07)
                                : allAbsent ? alpha(th.palette.error.main, 0.07)
                                : 'background.paper',
                            // Being edited: a blue outline all round, so it is clear which house is open
                            ...(editingCustId === cust.customer_id && {
                                borderColor: 'primary.main',
                                borderLeftColor: 'primary.main',
                                boxShadow: (th) => `0 0 0 1px ${th.palette.primary.main}`,
                            }),
                        }}>
                            <CardContent sx={{ p: '10px 12px 8px !important' }}>
                                {/* -- Customer header: identity on two lines, then the state control -- */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, pb: 0.75 }}>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body1" fontWeight={800} noWrap lineHeight={1.25}>
                                            {cust.customer_name}
                                        </Typography>
                                        {/* Mobile, door no and area on ONE line — three stacked
                                            lines were a third of the old card's height. The
                                            number dials: the whole thing is the tap target, since
                                            a 13px icon on its own is too small to hit outdoors. */}
                                        <Typography variant="caption" color="text.secondary" noWrap
                                            sx={{ display: 'block', lineHeight: 1.3 }}>
                                            {cust.customer_mobile && (
                                                <Box component="a" href={`tel:${cust.customer_mobile}`}
                                                    title={`Call ${cust.customer_mobile}`}
                                                    sx={{
                                                        color: 'primary.main', textDecoration: 'none',
                                                        fontWeight: 600, display: 'inline-flex',
                                                        alignItems: 'center', gap: 0.25,
                                                        verticalAlign: 'middle', py: 0.25,
                                                    }}>
                                                    <PhoneIcon sx={{ fontSize: 13 }} />
                                                    {cust.customer_mobile}
                                                </Box>
                                            )}
                                            {(() => {
                                                const rest = [cust.customer_address_line1,
                                                    areas.find(a => a.f_id === cust.area_id)?.f_name]
                                                    .filter(Boolean).join(' · ');
                                                if (!rest) return null;
                                                return cust.customer_mobile ? ` · ${rest}` : rest;
                                            })()}
                                        </Typography>
                                    </Box>

                                    {/* Delivered / Not delivered. Tapping the active one clears it
                                        back to "not reached yet". Hidden while the card is being edited,
                                        so Save / Cancel are the only things to press. */}
                                    <Box sx={{ display: editingCustId === cust.customer_id ? 'none' : 'flex', gap: 1, flexShrink: 0 }}>
                                        <IconButton size="small" title={isDelivered ? 'Tap to undo' : t('dailyOps.delivered')}
                                            disabled={cardBusy}
                                            onClick={() => setDelivered(cust, !isDelivered)}
                                            sx={(th) => ({
                                                width: 40, height: 40,
                                                bgcolor: isDelivered ? th.palette.success.main : alpha(th.palette.success.main, 0.12),
                                                color: isDelivered ? th.palette.common.white : th.palette.success.dark,
                                                '&:hover': { bgcolor: isDelivered ? th.palette.success.dark : alpha(th.palette.success.main, 0.2) },
                                            })}>
                                            {/* A plain tick, not CheckCircle: a filled green circle
                                                reads as "already done" even when unselected, which
                                                is the opposite of what an untouched house means.
                                                State is carried by the fill behind the icon. */}
                                            {actionLoading[deliveredKey]
                                                ? <CircularProgress size={14} color="inherit" />
                                                : <Check fontSize="small" />}
                                        </IconButton>

                                        {activeSubs.length > 0 && (
                                            <IconButton size="small" title={allAbsent ? 'Tap to undo' : t('dailyOps.notDelivered')}
                                                disabled={cardBusy}
                                                onClick={() => allAbsent ? undoAllAbsent(cust) : markCustomerAllAbsent(cust)}
                                                sx={(th) => ({
                                                    width: 40, height: 40,
                                                    bgcolor: allAbsent ? th.palette.error.main : alpha(th.palette.error.main, 0.1),
                                                    color: allAbsent ? th.palette.common.white : th.palette.error.dark,
                                                    '&:hover': { bgcolor: allAbsent ? th.palette.error.dark : alpha(th.palette.error.main, 0.18) },
                                                })}>
                                                {/* A cross pairs with the tick as one obvious yes/no.
                                                    PersonOff needs interpreting; ✓/✕ does not. */}
                                                {(actionLoading[allAbsentKey] || actionLoading[undoAllAbsentKey])
                                                    ? <CircularProgress size={14} color="inherit" />
                                                    : <Close fontSize="small" />}
                                            </IconButton>
                                        )}

                                    </Box>
                                </Box>

                                {/* -- Items: read view, or edited in place (one card at a time) -- */}
                                {cust.subscriptions.length === 0 ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                                        <Typography variant="caption" color="text.disabled">{t('dailyOps.noSubscriptions')}</Typography>
                                        <Button size="small" startIcon={<Add />} onClick={() => setPickerCustomer(cust)}
                                            sx={{ textTransform: 'none', fontWeight: 700, minHeight: TOUCH.dense }}>
                                            Add item
                                        </Button>
                                    </Box>
                                ) : (() => {
                                    // A paused plan isn't coming today, so it earns a mention, not a row.
                                    const pausedSubs = cust.subscriptions.filter(s => s.is_suspended);
                                    const liveSubs = cust.subscriptions.filter(s => !s.is_suspended);
                                    const editing = editingCustId === cust.customer_id;
                                    // The number in the box while editing: typed text, else today's count
                                    const editValue = (sub) => editQty[sub.product_id] ?? fmtCount(lineOf(sub).current);
                                    const editNumber = (sub) => {
                                        const n = parseFloat(editValue(sub));
                                        return Number.isFinite(n) ? n : 0;
                                    };
                                    const dayLines = liveSubs.filter(s => s.billing_type !== 'MonthlyFlatFee');
                                    const total = dayLines.reduce((n, s) => {
                                        const line = lineOf(s);
                                        const qty = editing && !line.isAbsent ? editNumber(s) : lineToday(s).qty;
                                        return n + qty * (s.price_per_unit || 0);
                                    }, 0);
                                    const priced = dayLines.some(s => (s.price_per_unit || 0) > 0);
                                    // Lines grouped by category like the round summary: each product's first
                                    // category, the vendor's category order, "No category" last. The server
                                    // already sends them in product order. Headings only when the card has
                                    // two or more categories — one heading on every card is just noise.
                                    const cardGroups = (() => {
                                        const byCat = {};
                                        const seen = [];
                                        for (const sb of liveSubs) {
                                            const c = sb.category || OTHER_CATEGORY;
                                            if (!byCat[c]) { byCat[c] = []; seen.push(c); }
                                            byCat[c].push(sb);
                                        }
                                        const order = [
                                            ...categoryOrder.filter(c => byCat[c] && c !== OTHER_CATEGORY),
                                            ...seen.filter(c => c !== OTHER_CATEGORY && !categoryOrder.includes(c)),
                                            ...(byCat[OTHER_CATEGORY] ? [OTHER_CATEGORY] : []),
                                        ];
                                        return order.map(c => ({ cat: c, subs: byCat[c] }));
                                    })();
                                    const showCardCats = cardGroups.length > 1;
                                    // A category's items and day amount, the same way the card total counts
                                    // them: monthly-rate items are items, but not part of a day's rupees.
                                    const groupTotals = (subs) => subs.reduce((acc, sb) => {
                                        const line = lineOf(sb);
                                        const flat = sb.billing_type === 'MonthlyFlatFee';
                                        const qty = editing && !flat && !line.isAbsent ? editNumber(sb) : lineToday(sb).qty;
                                        return { qty: acc.qty + qty, amount: acc.amount + (flat ? 0 : qty * (sb.price_per_unit || 0)) };
                                    }, { qty: 0, amount: 0 });
                                    // Items to hand over include monthly-fee ones (a monthly newspaper is
                                    // still delivered every day); the rupee total does not — that is billed
                                    // by the month.
                                    const itemCount = liveSubs.reduce((n, s) => {
                                        const line = lineOf(s);
                                        const editable = s.billing_type !== 'MonthlyFlatFee' && !line.isAbsent;
                                        return n + (editing && editable ? editNumber(s) : lineToday(s).qty);
                                    }, 0);

                                    return (
                                      <>
                                        {cardGroups.map(g => (
                                          <React.Fragment key={`cat-${g.cat}`}>
                                            {/* Category heading — same band as the round summary */}
                                            {showCardCats && (
                                                <Box sx={(th) => ({
                                                    mt: 0.5, px: 1, py: 0.4, borderLeft: 3, borderColor: 'primary.main',
                                                    borderRadius: 0.5, bgcolor: alpha(th.palette.primary.main, 0.08),
                                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
                                                })}>
                                                    {(() => {
                                                        const gt = groupTotals(g.subs);
                                                        return (
                                                            <>
                                                                <Typography variant="caption" fontWeight={800} color="primary.main" noWrap
                                                                    sx={{ textTransform: 'uppercase', letterSpacing: 0.5, minWidth: 0 }}>
                                                                    {g.cat} <Box component="span" sx={{ fontWeight: 700 }}>× {fmtCount(gt.qty)}</Box>
                                                                </Typography>
                                                                {gt.amount > 0 && (
                                                                    <Typography variant="caption" fontWeight={800} color="primary.main" sx={{ flexShrink: 0 }}>
                                                                        {rupees(gt.amount)}
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </Box>
                                            )}
                                        {g.subs.map(sub => {
                                            const isFlat = sub.billing_type === 'MonthlyFlatFee';
                                            const absentTxns = (sub.recorded_txns || []).filter(t => t.origin === 'Absent');
                                            const isAbsent = absentTxns.length > 0;
                                            const isNonSubscribed = sub.subscribed_qty === 0;
                                            const today = lineToday(sub);
                                            const plan = sub.subscribed_qty || 0;
                                            const diff = !isFlat && !isAbsent && !isNonSubscribed ? today.qty - plan : 0;
                                            const canEdit = !isFlat && !isAbsent;
                                            // Under the name: just the price, plus anything unusual about the line
                                            const note = [
                                                isFlat ? `${rupees(today.rate)} a month` : today.rate > 0 ? `${rupees(today.rate)} each` : null,
                                                isNonSubscribed && !isFlat ? 'added today' : null,
                                                sub.schedule_label && sub.schedule_label !== 'Every day' ? sub.schedule_label : null,
                                            ].filter(Boolean).join(' · ');
                                            // One pill style for every count: blue + ✎ when it can be changed here,
                                            // grey when not (monthly rate), red when not delivered.
                                            const pillTone = isAbsent ? 'error' : canEdit ? 'primary' : 'grey';
                                            return (
                                                <Box key={sub.product_id}
                                                    onClick={!editing && canEdit ? () => startEdit(cust, sub.product_id) : undefined}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 1, minHeight: 48, py: 0.5,
                                                        borderTop: 1, borderColor: 'divider',
                                                        cursor: !editing && canEdit ? 'pointer' : 'default',
                                                    }}>
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography variant="body2" fontWeight={600} noWrap lineHeight={1.35}>
                                                            {sub.product_name}
                                                        </Typography>
                                                        {note && (
                                                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.3 }}>
                                                                {note}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {/* Only appears under Show all — this one isn't due today */}
                                                    {sub.is_due_today === false && (
                                                        <Chip label="Not today" size="small" variant="outlined"
                                                            sx={{ height: 22, fontSize: '0.68rem', flexShrink: 0 }} />
                                                    )}
                                                    {/* Not delivered keeps its own undo */}
                                                    {isAbsent && (
                                                        <Chip
                                                            label={t('dailyOps.notDelivered')}
                                                            color="error" size="small"
                                                            onDelete={isFlat ? undefined : (e) => { e?.stopPropagation?.(); undoTxn(absentTxns[0].txn_id); }}
                                                            disabled={!!actionLoading[`undo-${absentTxns[0].txn_id}`]}
                                                            sx={{ height: 22, fontSize: '0.68rem', flexShrink: 0 }}
                                                        />
                                                    )}
                                                    {editing && canEdit ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                            <IconButton aria-label="Less" disabled={editSaving || editNumber(sub) <= 0}
                                                                onClick={() => setEditQty(q => ({ ...q, [sub.product_id]: fmtCount(Math.max(0, editNumber(sub) - 1)) }))}
                                                                sx={{ width: TOUCH.min, height: TOUCH.min }}>
                                                                <RemoveCircleOutline color={editNumber(sub) <= 0 ? 'disabled' : 'primary'} />
                                                            </IconButton>
                                                            <InputBase
                                                                value={editValue(sub)}
                                                                onChange={e => {
                                                                    const v = e.target.value;
                                                                    if (/^\d*\.?\d{0,3}$/.test(v)) setEditQty(q => ({ ...q, [sub.product_id]: v }));
                                                                }}
                                                                onFocus={e => e.target.select()}
                                                                autoFocus={editFocusId === sub.product_id}
                                                                disabled={editSaving}
                                                                inputProps={{ inputMode: 'decimal', 'aria-label': `${sub.product_name} count`,
                                                                    style: { textAlign: 'center', fontWeight: 800, padding: '6px 0' } }}
                                                                sx={{ width: 56, height: 36, fontSize: '1rem', border: '1.5px solid', borderColor: 'primary.main',
                                                                      borderRadius: 999, bgcolor: 'background.paper' }}
                                                            />
                                                            <IconButton aria-label="More" disabled={editSaving}
                                                                onClick={() => setEditQty(q => ({ ...q, [sub.product_id]: fmtCount(editNumber(sub) + 1) }))}
                                                                sx={{ width: TOUCH.min, height: TOUCH.min }}>
                                                                <AddCircleOutline color="primary" />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                                                            {/* Different from the plan today: a small +1 / −1 badge */}
                                                            {diff !== 0 && (
                                                                <Typography variant="caption" fontWeight={800}
                                                                    sx={(th) => ({
                                                                        px: 0.75, borderRadius: 999, lineHeight: 1.6,
                                                                        color: diff > 0 ? th.palette.success.dark : th.palette.warning.dark,
                                                                        bgcolor: alpha(diff > 0 ? th.palette.success.main : th.palette.warning.main, 0.14),
                                                                    })}>
                                                                    {diff > 0 ? `+${fmtCount(diff)}` : `−${fmtCount(-diff)}`}
                                                                </Typography>
                                                            )}
                                                            <Box component="span" sx={(th) => ({
                                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                                                                minWidth: 56, height: 32, px: 1.25, borderRadius: 999, fontWeight: 800,
                                                                color: pillTone === 'grey' ? th.palette.text.primary : th.palette[pillTone].dark,
                                                                bgcolor: pillTone === 'grey' ? th.palette.action.hover : alpha(th.palette[pillTone].main, 0.1),
                                                            })}>
                                                                <Typography component="span" variant="body1" fontWeight={800} color="inherit">
                                                                    {fmtCount(today.qty)}
                                                                </Typography>
                                                                {canEdit && <EditIcon sx={{ fontSize: 13, opacity: 0.75 }} />}
                                                            </Box>
                                                            <Typography variant="body2" fontWeight={isFlat ? 500 : 700}
                                                                color={isFlat ? 'text.secondary' : 'text.primary'}
                                                                sx={{ minWidth: 68, textAlign: 'right' }}>
                                                                {isFlat ? `${rupees(today.rate)}/mo` : today.rate > 0 ? rupees(today.amount) : ''}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                          </React.Fragment>
                                        ))}

                                        {/* Paused plans: one quiet line, however many there are */}
                                        {pausedSubs.length > 0 && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.75,
                                                       borderTop: 1, borderColor: 'divider', minWidth: 0 }}>
                                                <PauseCircleOutline sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
                                                <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                                                    {t('dailyOps.paused')}: {pausedSubs.map(s => s.product_name).join(', ')}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Footer band: items and total, then Collect — or Save / Cancel while editing.
                                            Monthly-rate items count as items but are billed by the month, not here. */}
                                        <Box sx={{ mx: '-12px', mb: '-8px', px: '12px', py: 1, mt: 0.5,
                                                   bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider' }}>
                                            {editing && (
                                                <Button size="small" startIcon={<Add />} disabled={editSaving}
                                                    onClick={() => { cancelEdit(); setPickerCustomer(cust); }}
                                                    sx={{ textTransform: 'none', fontWeight: 700, minHeight: TOUCH.dense, mb: 0.5 }}>
                                                    Add another item
                                                </Button>
                                            )}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                {!editing && (paidByCustomer[cust.customer_id] || 0) > 0 && (
                                                    <Chip size="small" color="success" icon={<CheckCircle />}
                                                        label={`Paid ${rupees(paidByCustomer[cust.customer_id])}`}
                                                        sx={{ height: 26, fontWeight: 700 }} />
                                                )}
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
                                                        {fmtCount(itemCount)} {itemCount === 1 ? 'item' : 'items'}
                                                    </Typography>
                                                    {priced && (
                                                        <Typography variant="body1" fontWeight={800} sx={{ lineHeight: 1.25 }}>
                                                            {rupees(total)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Box sx={{ flex: 1 }} />
                                                {editing ? (
                                                    <>
                                                        <Button size="small" onClick={cancelEdit} disabled={editSaving}
                                                            sx={{ textTransform: 'none', minHeight: TOUCH.dense }}>
                                                            Cancel
                                                        </Button>
                                                        <Button size="small" variant="contained" color="success" disableElevation
                                                            onClick={() => saveEdit(cust, liveSubs)} disabled={editSaving}
                                                            sx={{ textTransform: 'none', fontWeight: 700, minHeight: TOUCH.dense, minWidth: 80, borderRadius: 999 }}>
                                                            {editSaving ? <CircularProgress size={16} color="inherit" /> : 'Save'}
                                                        </Button>
                                                    </>
                                                ) : (
                                                    <>
                                                        {/* No count to tap (only monthly items, or nothing delivered):
                                                            still a way to add an item */}
                                                        {!liveSubs.some(sb => sb.billing_type !== 'MonthlyFlatFee' && !lineOf(sb).isAbsent) && (
                                                            <Button size="small" startIcon={<Add />} onClick={() => setPickerCustomer(cust)}
                                                                sx={{ textTransform: 'none', fontWeight: 700, minHeight: TOUCH.dense }}>
                                                                Add item
                                                            </Button>
                                                        )}
                                                        {priced && (
                                                            <Button size="small" variant="contained" color="success" disableElevation
                                                                startIcon={<CurrencyRupee sx={{ fontSize: '1rem !important' }} />}
                                                                onClick={() => setPayCustomer({
                                                                    ...cust,
                                                                    total,
                                                                    lines: dayLines
                                                                        .map(s => ({ name: s.product_name, ...lineToday(s) }))
                                                                        .filter(l => l.qty > 0),
                                                                })}
                                                                sx={{ minHeight: TOUCH.dense, px: 1.75, fontWeight: 700, textTransform: 'none', borderRadius: 999 }}>
                                                                Collect
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                      </>
                                    );
                                })()}
                            </CardContent>
                        </Card>
                    );
                })}
                {displayedCustomers.length > visibleCount && (
                    <Box sx={{ textAlign: 'center', mt: 1, mb: 2 }}>
                        <Button variant="outlined" size="small" onClick={() => setVisibleCount(v => v + 50)}>
                            Load more ({displayedCustomers.length - visibleCount} remaining)
                        </Button>
                    </Box>
                )}
                </>
            )}

            {/* Collect a payment on the doorstep, prefilled from the card */}
            <DailyOpsPaymentDialog
                businessId={businessId}
                business={business}
                customer={payCustomer}
                date={date}
                paidToday={payCustomer ? (paidByCustomer[payCustomer.customer_id] || 0) : 0}
                onClose={() => setPayCustomer(null)}
                onSaved={loadPaid}
            />

            {/* Plan items first, then every other product by category */}
            <DailyOpsProductPicker
                customer={pickerCustomer}
                products={allProducts}
                categoryOrder={categoryOrder}
                date={date}
                onClose={() => setPickerCustomer(null)}
                onSave={changes => saveItemChanges(pickerCustomer, changes)}
            />
        </Box>
    );
}
